import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { reviewsAPI } from '../../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Star, User, Clock, Check, AlertTriangle, MessageSquare, Loader2 } from 'lucide-react';

const ReviewDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReview();
  }, [id]);

  const fetchReview = async () => {
    try {
      const res = await reviewsAPI.getReview(id);
      setReview(res.data);
    } catch (error) {
      console.error('Error fetching review:', error);
      toast.error('Failed to load review');
      navigate('/employer/reviews');
    } finally {
      setLoading(false);
    }
  };

  const getCadenceBadge = (cadence) => {
    const colors = {
      'one-time': 'bg-[#F5F8FF] text-[#5B6472]',
      'weekly': 'bg-blue-500/20 text-blue-400',
      'monthly': 'bg-purple-500/20 text-purple-400',
      'quarterly': 'bg-green-500/20 text-green-400'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm ${colors[cadence] || colors['one-time']}`}>
        {cadence?.charAt(0).toUpperCase() + cadence?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!review) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="review-detail">
        {/* Header */}
        <div className="animate-fade-in">
          <Button
            variant="ghost"
            onClick={() => navigate('/employer/reviews')}
            className="text-[#5B6472] hover:text-[#0A0F1E] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Reviews
          </Button>
        </div>

        {/* Employee Info Card */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                {review.employee_photo ? (
                  <img src={review.employee_photo} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-[#5B6472]" />
                )}
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#0A0F1E]">
                  {review.employee_name || 'Employee'}
                </h1>
                <p className="text-[#5B6472]">{review.employee_title}</p>
                <div className="flex items-center gap-2 mt-2">
                  {getCadenceBadge(review.review_type)}
                  {review.status === 'pending' ? (
                    <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Pending
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Acknowledged
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[#5B6472] text-sm">Review Date</p>
              <p className="text-[#0A0F1E] font-medium">{new Date(review.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </Card>

        {/* Performance Rating */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Performance Rating</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((rating) => (
                <Star 
                  key={rating}
                  className={`w-8 h-8 ${
                    rating <= review.performance_rating 
                      ? 'fill-[#1B5AFF] text-[#1B5AFF]' 
                      : 'text-[#5B6472]'
                  }`} 
                />
              ))}
            </div>
            <span className="text-2xl font-bold text-[#0A0F1E]">{review.performance_rating}/5</span>
          </div>
        </Card>

        {/* Review Content */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Key Achievements */}
          {review.key_achievements && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
              <h3 className="text-lg font-semibold text-[#0A0F1E] mb-3">Key Achievements</h3>
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{review.key_achievements}</p>
            </Card>
          )}

          {/* Areas to Improve */}
          {review.areas_to_improve && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
              <h3 className="text-lg font-semibold text-[#0A0F1E] mb-3">Areas to Improve</h3>
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{review.areas_to_improve}</p>
            </Card>
          )}
        </div>

        {/* Goals */}
        {review.goals_next_period && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h3 className="text-lg font-semibold text-[#0A0F1E] mb-3">Goals for Next Period</h3>
            <p className="text-[#0A0F1E] whitespace-pre-wrap">{review.goals_next_period}</p>
          </Card>
        )}

        {/* Manager Notes */}
        {review.manager_notes && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Manager Notes</h3>
              {review.share_manager_notes ? (
                <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs">Shared with employee</span>
              ) : (
                <span className="px-2 py-1 rounded-full bg-[#F5F8FF] text-[#5B6472] text-xs">Internal only</span>
              )}
            </div>
            <p className="text-[#0A0F1E] whitespace-pre-wrap">{review.manager_notes}</p>
          </Card>
        )}

        {/* Employee Self-Assessment */}
        {review.self_assessment && (
          <Card className="bg-[#FFFFFF] border border-green-500/30 rounded-xl p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-5 h-5 text-green-400" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Employee Self-Assessment</h3>
            </div>
            <p className="text-[#0A0F1E] whitespace-pre-wrap">{review.self_assessment}</p>
          </Card>
        )}

        {/* Flags */}
        {review.flags && review.flags.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-red-500/30 rounded-xl p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Disputed Fields</h3>
            </div>
            <div className="space-y-3">
              {review.flags.map((flag) => (
                <div key={flag.id} className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <p className="text-red-400 font-medium text-sm mb-1">Disputed: {flag.field_name}</p>
                  <p className="text-[#0A0F1E] text-sm">{flag.employee_message}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Acknowledged Info */}
        {review.status === 'acknowledged' && review.acknowledged_at && (
          <div className="text-center text-[#5B6472] text-sm">
            Acknowledged by employee on {new Date(review.acknowledged_at).toLocaleDateString()}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReviewDetail;
